import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import uniqBy from "lodash/uniqBy";
import { REQUEST_LOADING_STATES } from "../../helpers/constant";
import { ICollectionsState, IDeleteResponse } from "../../helpers/types";
import { collectionService } from "../../services/collection.service";

const initialState: ICollectionsState = {
  userCollections: null,
  otherUserCollections: [],
  loading: REQUEST_LOADING_STATES.IDLE,
  error: null,
  shouldNavigateToPublicationPage: false
};

export const getUserCollections = createAsyncThunk("collections/getUserCollections", async (page: number) => {
  const data = await collectionService.getCreatorCollections(page);
  return {...data, page};
});

export const getOtherUserCollections = createAsyncThunk("collections/getOtherUserCollections",async ({ creatorId, page }: { creatorId: string; page: number }) => {
    const data = await collectionService.getFanCollections(creatorId, page);
    return {...data, page};
  });

export const deleteCollectionById = createAsyncThunk<IDeleteResponse, string>("collections/deleteCollection",async (collectionId: string) => {
    return await collectionService.deleteCollection(collectionId);
  });

const collectionsSlice = createSlice({
  name: "collections",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getOtherUserCollections.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const collections = action.payload.data;
      const total = action?.payload?.total;
      const totalPages = action?.payload?.totalPages;

      const otherUserCollections = state.otherUserCollections;
      const user = otherUserCollections.findIndex((user) => user.id === collections[0].creatorId);
      if (user !== -1) {
        const currentPage = otherUserCollections[user].page;
        const hasMore = currentPage  < totalPages;
        otherUserCollections[user] = {
          ...otherUserCollections[user],
          collections: uniqBy([...otherUserCollections[user].collections, ...collections], "_id"),
          ttl: 5,
          lft: new Date().getTime(),
          hasMore,
          total,
          page: hasMore ? currentPage + 1 : currentPage
        };
        state.otherUserCollections = otherUserCollections;
      } else {
        const currentPage = action.payload.page;
        if(currentPage === 1 && collections.length === 0) {
          state.shouldNavigateToPublicationPage = true;
        } else {
          const hasMore = currentPage  < totalPages;
          state.otherUserCollections = [...otherUserCollections, { 
            id: collections[0].creatorId,
            collections: collections,
            ttl: 5,
            lft: new Date().getTime(),
            hasMore,
            total,
            page: hasMore ? currentPage + 1 : currentPage
          }]; 
        }
      }
    });

    builder.addCase(getUserCollections.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const currentCollections = state.userCollections;
      const collections = action.payload.data;
      const total = action?.payload?.total;
      const totalPages = action?.payload?.totalPages;

      if(currentCollections) {
        const currentPage = currentCollections.page;
        const hasMore = currentPage  < totalPages;
        state.userCollections = {
          ...currentCollections,
          collections: uniqBy([...currentCollections.collections, ...collections], "_id"),
          ttl: 5,
          lft: new Date().getTime(),
          page: hasMore ? currentPage + 1 : currentPage,
          hasMore,
          total
        };
      } else {
        const currentPage = action.payload.page;
        if(currentPage === 1 && collections.length === 0) {
          state.shouldNavigateToPublicationPage = true;
        }else {
          const hasMore = currentPage  < totalPages;
          state.userCollections = {
            id: '',
            collections: collections,
            hasMore,
            ttl: 5,
            lft: new Date().getTime(),
            page: hasMore ? currentPage + 1 : currentPage,
            total
          };
        }
      }
    });
    
    builder.addCase(deleteCollectionById.fulfilled, (state, action) => {

    });

    builder.addMatcher(isAnyOf(getUserCollections.pending, getOtherUserCollections.pending), (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
    });

    builder.addMatcher(isAnyOf(getOtherUserCollections.rejected, getUserCollections.rejected), (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export default collectionsSlice.reducer;
